import { useCallback } from 'react';
import { loginWithEmail } from '../application/authCredentials';
import {
  normalizeEmail,
  validateLoginInput,
  type AuthFormValidationResult,
} from '../application/authFormValidation';
import { useAuthStore } from '../store/useAuthStore';
import { showInfo, showError } from '../../shared/ui/appToast';
import { devLog } from '../../shared/dev/devLog';

function applyValidationFailure(result: Extract<AuthFormValidationResult, { ok: false }>) {
  useAuthStore.getState().setError(result.error);
  if (result.toast.kind === 'info') {
    showInfo(result.toast.title, result.toast.body);
  } else {
    showError(result.toast.title, result.toast.body);
  }
}

type Options = {
  email: string;
  password: string;
  /** Login bloqueado: mail sin confirmar → pantalla de reenvío */
  onEmailConfirmationRequired?: (email: string) => void;
};

/**
 * Hook de login: valida email/contraseña y dispara `loginWithEmail`.
 *
 * El estado de los campos (email/password) lo mantiene la pantalla y se pasa
 * como params; el hook se encarga de la validación + llamada al servicio de
 * auth + propagación de errores al store y toasts.
 */
export function useLoginForm({ email, password, onEmailConfirmationRequired }: Options) {
  const handleLogin = useCallback(async () => {
    const result = validateLoginInput(email, password);
    if (!result.ok) {
      applyValidationFailure(result);
      return;
    }
    const normalized = normalizeEmail(email);
    try {
      await loginWithEmail(normalized, password);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === 'email_not_confirmed' && onEmailConfirmationRequired) {
        onEmailConfirmationRequired(normalized);
        return;
      }
      devLog('[LoginScreen] Error login', err);
    }
  }, [email, password, onEmailConfirmationRequired]);

  return { handleLogin };
}
